import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Mail, Shield, Loader2, Power, PauseCircle, KeyRound, X, Bell, Check } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, Th, Td, AdminHeading, adminInput } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_COLORS = {
  Active: "text-green-300 bg-green-400/10", Invited: "text-pool bg-pool/10",
  Deactivated: "text-muted-foreground bg-white/5", Suspended: "text-red-300 bg-red-400/10",
};

function StaffDialog({ open, onOpenChange, roles, teams, mode, initial, onDone }) {
  const invite = mode === "invite";
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", job_title: "",
    role: "read_only", team: "", password: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { if (open) setF({ first_name: initial?.first_name || "", last_name: initial?.last_name || "",
    email: initial?.email || "", phone: initial?.phone || "", job_title: initial?.job_title || "",
    role: initial?.role || "read_only", team: initial?.team || "", password: "" }); }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      if (mode === "edit") { await api.put(`/admin/staff/${initial.id}`, f); toast.success("Staff updated."); }
      else if (invite) { const { data } = await api.post("/admin/staff/invite", f); toast.success("Invitation sent."); }
      else { await api.post("/admin/staff", f); toast.success("Staff added."); }
      onOpenChange(false); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10 max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">
          {mode === "edit" ? "Edit Staff" : invite ? "Invite Staff" : "Add Staff"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input className={adminInput} placeholder="First name" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="staff-first" />
            <input className={adminInput} placeholder="Last name" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="staff-last" />
          </div>
          <input className={adminInput} placeholder="Email" value={f.email} disabled={mode === "edit"} onChange={(e) => set("email", e.target.value)} data-testid="staff-email" />
          <div className="grid grid-cols-2 gap-3">
            <input className={adminInput} placeholder="Mobile phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
            <input className={adminInput} placeholder="Job title" value={f.job_title} onChange={(e) => set("job_title", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select className={adminInput} value={f.role} onChange={(e) => set("role", e.target.value)} data-testid="staff-role">
              {roles.filter((r) => r.key !== "owner").map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <select className={adminInput} value={f.team} onChange={(e) => set("team", e.target.value)} data-testid="staff-team">
              <option value="">No team</option>{teams.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {mode === "add" && (
            <input type="password" className={adminInput} placeholder="Temporary password (8+ chars, upper/lower/number)" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="staff-password" />
          )}
          {invite && <p className="text-xs text-muted-foreground">An invitation email with a secure setup link will be sent. The account stays limited to the assigned role until accepted.</p>}
          <button onClick={save} disabled={saving || !f.email} className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2" data-testid="staff-save">
            {saving ? <Loader2 className="animate-spin" size={15} /> : invite ? <Mail size={15} /> : <UserPlus size={15} />}
            {mode === "edit" ? "Save Changes" : invite ? "Send Invitation" : "Add Staff"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StaffPrefsModal({ staff, onClose }) {
  const [prefs, setPrefs] = useState(null);
  const [events, setEvents] = useState([]);
  const [smsCfg, setSmsCfg] = useState(false);
  useEffect(() => {
    if (!staff) { setPrefs(null); return; }
    api.get(`/admin/notification-preferences?staff_id=${staff.id}`).then((r) => { setPrefs(r.data.prefs); setEvents(r.data.events); setSmsCfg(r.data.sms_configured); }).catch(() => {});
  }, [staff]);
  const setChan = (k, ch, v) => setPrefs((p) => ({ ...p, [k]: { ...p[k], [ch]: v } }));
  const save = async () => { try { await api.put("/admin/notification-preferences", { staff_id: staff.id, prefs }); toast.success("Preferences saved."); onClose(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Dialog open={!!staff} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-surface border-white/10 max-w-lg" data-testid="staff-prefs-modal">
        <DialogHeader><DialogTitle className="font-serif text-2xl flex items-center gap-2"><Bell size={18} /> Notification Preferences</DialogTitle></DialogHeader>
        {!prefs ? <Loading /> : (
          <div>
            <p className="text-xs text-muted-foreground mb-2">{[staff.first_name, staff.last_name].filter(Boolean).join(" ") || staff.email}. {smsCfg ? "" : "SMS Not Configured."} Required operational notices always appear in-app.</p>
            <div className="grid grid-cols-12 gap-1 text-[10px] uppercase tracking-label text-muted-foreground border-b border-white/10 pb-1.5 mb-1"><div className="col-span-6">Event</div><div className="col-span-2 text-center">In-App</div><div className="col-span-2 text-center">Email</div><div className="col-span-2 text-center">SMS</div></div>
            <div className="max-h-72 overflow-y-auto">{events.map((ev) => (
              <div key={ev.key} className="grid grid-cols-12 gap-1 items-center py-1 border-b border-white/5 text-xs" data-testid={`staffpref-${ev.key}`}>
                <div className="col-span-6">{ev.label}{ev.required && <span className="text-[9px] text-red-300 ml-1">required</span>}</div>
                <div className="col-span-2 text-center"><input type="checkbox" className="accent-gold" checked={!!prefs[ev.key]?.in_app || ev.required} disabled={ev.required} onChange={(e) => setChan(ev.key, "in_app", e.target.checked)} /></div>
                <div className="col-span-2 text-center"><input type="checkbox" className="accent-gold" checked={!!prefs[ev.key]?.email} onChange={(e) => setChan(ev.key, "email", e.target.checked)} /></div>
                <div className="col-span-2 text-center"><input type="checkbox" className="accent-gold" checked={!!prefs[ev.key]?.sms} disabled={!smsCfg} onChange={(e) => setChan(ev.key, "sms", e.target.checked)} /></div>
              </div>
            ))}</div>
            <button onClick={save} className="btn-gold w-full py-2.5 text-sm mt-3 flex items-center justify-center gap-2" data-testid="staff-prefs-save"><Check size={15} /> Save Preferences</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


export default function AdminStaff() {
  const [staff, setStaff] = useState(null);
  const [meta, setMeta] = useState({ roles: [], teams: [] });
  const [dlg, setDlg] = useState({ open: false, mode: "add", initial: null });
  const [prefsFor, setPrefsFor] = useState(null);
  const roleLabel = (k) => meta.roles.find((r) => r.key === k)?.label || k;

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => setStaff([]));
  useEffect(() => { api.get("/admin/staff/roles").then((r) => setMeta(r.data)).catch(() => {}); load(); }, []);

  const act = async (u, action) => {
    try { await api.post(`/admin/staff/${u.id}/status`, { action }); toast.success(`Staff ${action}d.`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const reset = async (u) => {
    try { await api.post(`/admin/staff/${u.id}/reset-access`); toast.success("Reset link sent."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <AdminHeading title="Staff & Teams" subtitle={`${staff?.length ?? 0} team members`}>
        <div className="flex gap-2">
          <button onClick={() => setDlg({ open: true, mode: "invite", initial: null })} className="btn-ghost-gold px-4 py-2.5 text-sm flex items-center gap-2" data-testid="staff-invite-btn"><Mail size={15} /> Invite Staff</button>
          <button onClick={() => setDlg({ open: true, mode: "add", initial: null })} className="btn-gold px-4 py-2.5 text-sm flex items-center gap-2" data-testid="staff-add-btn"><UserPlus size={15} /> Add Staff</button>
        </div>
      </AdminHeading>
      <Panel>
        {staff === null ? <Loading /> : staff.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No staff yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead><tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Team</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr></thead>
              <tbody>{staff.map((u) => (
                <tr key={u.id} className="hover:bg-white/5" data-testid={`staff-row-${u.id}`}>
                  <Td className="whitespace-nowrap">{[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"}{u.protected && <span className="ml-2 text-[10px] text-gold">OWNER</span>}</Td>
                  <Td className="text-muted-foreground">{u.email}</Td>
                  <Td><span className="inline-flex items-center gap-1.5 text-gold"><Shield size={12} /> {roleLabel(u.role)}</span></Td>
                  <Td className="text-muted-foreground">{u.team || "—"}</Td>
                  <Td><span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COLORS[u.account_status] || "text-muted-foreground bg-white/5"}`}>{u.account_status}</span></Td>
                  <Td className="text-right whitespace-nowrap">
                    {!u.protected && <>
                      <button title="Edit" onClick={() => setDlg({ open: true, mode: "edit", initial: u })} className="text-muted-foreground hover:text-pool p-1.5" data-testid={`staff-edit-${u.id}`}><Shield size={15} /></button>
                      {u.account_status !== "Suspended" && <button title="Suspend" onClick={() => act(u, "suspend")} className="text-muted-foreground hover:text-red-300 p-1.5" data-testid={`staff-suspend-${u.id}`}><PauseCircle size={15} /></button>}
                      {u.account_status === "Active"
                        ? <button title="Deactivate" onClick={() => act(u, "deactivate")} className="text-muted-foreground hover:text-red-300 p-1.5" data-testid={`staff-deactivate-${u.id}`}><Power size={15} /></button>
                        : <button title="Reinstate" onClick={() => act(u, "reinstate")} className="text-muted-foreground hover:text-green-300 p-1.5" data-testid={`staff-reinstate-${u.id}`}><Power size={15} /></button>}
                      <button title="Reset access" onClick={() => reset(u)} className="text-muted-foreground hover:text-gold p-1.5" data-testid={`staff-reset-${u.id}`}><KeyRound size={15} /></button>
                      <button title="Notification preferences" onClick={() => setPrefsFor(u)} className="text-muted-foreground hover:text-pool p-1.5" data-testid={`staff-prefs-${u.id}`}><Bell size={15} /></button>
                    </>}
                    {u.protected && <span className="text-[10px] text-muted-foreground">Protected</span>}
                  </Td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Panel>
      <StaffDialog open={dlg.open} onOpenChange={(o) => setDlg((p) => ({ ...p, open: o }))} roles={meta.roles} teams={meta.teams} mode={dlg.mode} initial={dlg.initial} onDone={load} />
      <StaffPrefsModal staff={prefsFor} onClose={() => setPrefsFor(null)} />
    </div>
  );
}
